/**
 * Service Worker 管理器
 * 处理PWA功能、缓存管理和离线支持
 */

class ServiceWorkerManager {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator
    this.registration = null
    this.isOnline = navigator.onLine
    
    if (this.isSupported) {
      this.init()
    }
  }

  async init() {
    try {
      // 注册Service Worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      })
      
      console.log('Service Worker registered successfully')
      
      // 监听更新
      this.registration.addEventListener('updatefound', () => {
        this.handleUpdate()
      })
      
      // 监听网络状态变化
      window.addEventListener('online', () => {
        this.isOnline = true
        this.handleOnlineStatusChange(true)
      })
      
      window.addEventListener('offline', () => {
        this.isOnline = false
        this.handleOnlineStatusChange(false)
      })
      
    } catch (error) {
      console.error('Service Worker registration failed:', error)
    }
  }

  handleUpdate() {
    const newWorker = this.registration.installing
    
    if (newWorker) {
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // 有新版本可用
          this.showUpdateNotification()
        }
      })
    }
  }

  showUpdateNotification() {
    // 可以在这里显示更新提示
    console.log('新版本可用，请刷新页面')
    
    // 如果使用Element UI
    if (window.Vue && window.Vue.prototype.$message) {
      window.Vue.prototype.$message({
        message: '发现新版本，建议刷新页面以获得最佳体验',
        type: 'info',
        duration: 5000,
        showClose: true
      })
    }
  }

  handleOnlineStatusChange(isOnline) {
    console.log('网络状态变化:', isOnline ? '在线' : '离线')
    
    if (window.Vue && window.Vue.prototype.$message) {
      window.Vue.prototype.$message({
        message: isOnline ? '网络已连接' : '网络已断开，切换到离线模式',
        type: isOnline ? 'success' : 'warning',
        duration: 3000
      })
    }
  }

  // 手动更新Service Worker
  async updateServiceWorker() {
    if (this.registration) {
      try {
        await this.registration.update()
        console.log('Service Worker update triggered')
      } catch (error) {
        console.error('Service Worker update failed:', error)
      }
    }
  }

  // 获取缓存使用情况
  async getCacheUsage() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate()
        return {
          used: estimate.usage,
          available: estimate.quota,
          usagePercentage: ((estimate.usage / estimate.quota) * 100).toFixed(2)
        }
      } catch (error) {
        console.error('Failed to get cache usage:', error)
        return null
      }
    }
    return null
  }

  // 清理缓存
  async clearCache() {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        )
        console.log('所有缓存已清理')
        return true
      } catch (error) {
        console.error('清理缓存失败:', error)
        return false
      }
    }
    return false
  }

  // 预缓存关键资源
  async precacheResources(urls) {
    if ('caches' in window && urls.length > 0) {
      try {
        const cache = await caches.open('precache-v1.0.0')
        await cache.addAll(urls)
        console.log('关键资源预缓存完成')
        return true
      } catch (error) {
        console.error('预缓存失败:', error)
        return false
      }
    }
    return false
  }

  // 检查资源是否已缓存
  async isResourceCached(url) {
    if ('caches' in window) {
      try {
        const response = await caches.match(url)
        return !!response
      } catch (error) {
        console.error('检查缓存失败:', error)
        return false
      }
    }
    return false
  }

  // 获取缓存统计信息
  async getCacheStats() {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys()
        const stats = {}
        
        for (const cacheName of cacheNames) {
          const cache = await caches.open(cacheName)
          const keys = await cache.keys()
          stats[cacheName] = keys.length
        }
        
        return stats
      } catch (error) {
        console.error('获取缓存统计失败:', error)
        return {}
      }
    }
    return {}
  }
}

// 创建全局实例
export const serviceWorkerManager = new ServiceWorkerManager()

// Vue插件
export const ServiceWorkerPlugin = {
  install(Vue) {
    Vue.prototype.$sw = serviceWorkerManager
    
    // 添加全局方法
    Vue.mixin({
      created() {
        // 在开发环境下添加调试方法
        if (process.env.NODE_ENV !== 'production') {
          window.showCacheStats = async () => {
            const stats = await serviceWorkerManager.getCacheStats()
            const usage = await serviceWorkerManager.getCacheUsage()
            console.log('缓存统计:', stats)
            console.log('存储使用:', usage)
          }
          
          window.clearAllCache = () => {
            serviceWorkerManager.clearCache()
          }
        }
      }
    })
  }
}

export default serviceWorkerManager
